import { ConfirmationModalComponent, TooltipedTextComponent } from 'commons/components';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { executeRequest } from 'core/utils/backend-service';
import { showSuccessNotification } from 'core/utils/notification-service';
import { useConfigurationList } from 'modules/settings/contexts/device-configuration/hooks/use-configuration-list.hook';
import { usePopupManager } from 'react-popup-manager';
import { updateConfigurationInstancesActiveStatus } from '../service/settings-devices-configuration.service';
import { DeviceConfigurationInfo } from '../settings-devices-configuration-list/settings-devices-configuration-list.model';
import { sortAndJoin } from 'commons/utils/sort.utils';

export const useActiveDeactivateDeviceConfiguration = () => {
    const { t } = useTranslation();
    const popupManager = usePopupManager();
    const { refreshConfigurationList } = useConfigurationList();

    const { mutateAsync: updateActiveStatus } = usePocMutation({
        mutationFn: ({ ids, active }: { ids: string[]; active: boolean }) =>
            executeRequest(() => updateConfigurationInstancesActiveStatus(ids, active)),
    });

    const confirmationModal = (deviceConfigs: DeviceConfigurationInfo[] = []) => {
        const status = deviceConfigs.every((config) => config.active) ? 'deactivate' : 'activate';

        popupManager.open(ConfirmationModalComponent, {
            title:
                deviceConfigs.length === 1 ? (
                    <>
                        {t(
                            `settings.devices.device-settings.device-config-list.action.${status}.confirmation-modal.title`,
                            { name: deviceConfigs[0].name }
                        )}
                    </>
                ) : (
                    <TooltipedTextComponent
                        i18nKey={`settings.devices.device-settings.device-config-list.action.${status}.multiple.confirmation-modal.title`}
                        tooltipText={sortAndJoin(deviceConfigs.map((dev) => `${dev.name}`))}
                        i18nValues={{ number: deviceConfigs.length }}
                        tooltipAnchorClassname="blue-tooltip-span"
                        showTooltipAlways
                    />
                ),
            children: (
                <div style={{ display: 'flex', flexDirection: 'column', rowGap: '16px' }}>
                    <span>
                        {t(
                            `settings.devices.device-settings.device-config-list.action.${status}.confirmation-modal.description`
                        )}
                    </span>
                </div>
            ),
            buttonText: t(
                `settings.devices.device-settings.device-config-list.action.${status}.confirmation-modal.confirm`
            ),
            cancelButtonText: t(
                `settings.devices.device-settings.device-config-list.action.${status}.confirmation-modal.discard`
            ),
            actionOnClick: () => {
                const ids = deviceConfigs.map((v) => v.id);
                return updateActiveStatus({ ids, active: status === 'activate' }).then(() => {
                    showSuccessNotification(
                        t(
                            `settings.devices.device-settings.device-config-list.action.${status}.confirmation-modal.success`
                        )
                    );
                    const DEVICE_REALLOCATION_DELAY = 1200;
                    refreshConfigurationList({ fetchingDelay: DEVICE_REALLOCATION_DELAY });
                });
            },
            onClose: () => {},
        });
    };

    const hasDefaultConfig = (deviceConfigs: DeviceConfigurationInfo[] = []) =>
        deviceConfigs.some((config) => config.isByDefault);

    const handleActivateDeactivate = (deviceConfigs: DeviceConfigurationInfo[] = []) => {
        if (!hasDefaultConfig(deviceConfigs)) {
            confirmationModal(deviceConfigs);
        }
    };

    return {
        handleActivateDeactivate,
        hasDefaultConfig,
    };
};
